import { useMemo } from 'react';
import { Table, TableData } from '@mantine/core';
import {
  CellRendererSelectorResult,
  ICellRendererParams,
} from 'ag-grid-community';

import { GoogleFontIcon } from '../../icons/GoogleFontIcon';
import { Popover } from '../../popover/Popover';

import { DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED } from './constants';

import styles from './detailPaneCellRenderer.module.scss';

export type DetailPaneProps = {
  tableData: TableData['body'];
  headline: string;
};

function DetailPaneContent({ headline, tableData }: DetailPaneProps) {
  return (
    <>
      {headline && (
        <div className={styles['detailPane__headline']}>{headline}</div>
      )}
      {tableData && tableData.length > 0 && (
        <Table
          classNames={{
            table: styles['detailPane__table'],
            tr: styles['detailPane__tr'],
            td: styles['detailPane__td'],
          }}
          data={{ body: tableData }}
          unstyled
          withRowBorders={false}
        />
      )}
    </>
  );
}

export type DetailPaneCellRendererDecoratorProps = {
  cellRendererConfig?: CellRendererSelectorResult;
} & ICellRendererParams &
  DetailPaneProps;

export const DetailPaneCellRendererDecorator = ({
  headline,
  cellRendererConfig,
  tableData,
  ...cellRendererProps
}: DetailPaneCellRendererDecoratorProps) => {
  const parent = useMemo(() => {
    if (cellRendererConfig != null) {
      return (
        <cellRendererConfig.component
          {...cellRendererConfig.params}
          {...cellRendererProps}
        />
      );
    }

    return;
  }, [cellRendererConfig, cellRendererProps]);

  const { value, valueFormatted } = cellRendererProps;

  if (value === undefined) {
    return parent;
  }

  const cellValue = valueFormatted ?? value;

  return (
    <div className={styles['root']} data-testid="detail-pane-cell">
      <span className={styles['value']}>{parent ?? cellValue}</span>
      <Popover
        content={
          <DetailPaneContent headline={headline} tableData={tableData} />
        }
      >
        <GoogleFontIcon
          {...DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED}
          name="info"
          className={styles['icon']}
        />
      </Popover>
    </div>
  );
};
